import type { GlobalState } from '@affine/core/modules/storage';
import type { WorkspaceQuotaQuery } from '@affine/graphql';
import { workspaceQuotaQuery } from '@affine/graphql';
import { Store } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud/services/workspace-server';

type QuotaType = WorkspaceQuotaQuery['workspace']['quota'];

export const getWorkspaceQuotaCacheKey = (
  serverId: string,
  workspaceId: string,
  accountId: string
) => `streamlain:workspace-quota:${serverId}:${workspaceId}:${accountId}`;

export class WorkspaceQuotaStore extends Store {
  constructor(
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly globalState: GlobalState
  ) {
    super();
  }

  private get serverCacheScope() {
    return (
      this.workspaceServerService.server?.id ??
      this.workspaceServerService.server?.baseUrl ??
      'unbound-server'
    );
  }

  get currentAccountId(): string | undefined {
    return this.workspaceServerService.server?.account$.value?.id;
  }

  getCachedWorkspaceQuota(
    workspaceId: string,
    accountId: string
  ): QuotaType | null {
    return (
      this.globalState.get<QuotaType>(
        getWorkspaceQuotaCacheKey(this.serverCacheScope, workspaceId, accountId)
      ) ?? null
    );
  }

  async fetchWorkspaceQuota(
    workspaceId: string,
    accountId: string,
    signal?: AbortSignal
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    const data = await this.workspaceServerService.server.gql({
      query: workspaceQuotaQuery,
      variables: {
        id: workspaceId,
      },
      context: {
        signal,
      },
    });
    this.globalState.set(
      getWorkspaceQuotaCacheKey(this.serverCacheScope, workspaceId, accountId),
      data.workspace.quota
    );
    return data.workspace.quota;
  }
}
